/**
 * @file BinLevelPicker.tsx
 * @description Плитки ячеек полки / полок стеллажа после скана уровня
 *   (phase19.22, W14-9; ui-design §8): «Полка A-02 — выберите ячейку:
 *   [A-02-01 · пусто] [A-02-03 · 2 п.]».
 *
 * Скан уровня — не выбранный адрес: пока кладовщик не тапнул плитку, ячейки
 * нет, и заголовок это говорит прямо. Плитки 56px, 2 в ряд на телефоне,
 * больше — на широком экране.
 */

import { ChevronRight, X } from "lucide-react";

import { Button } from "../../ui/button";
import type { ResolvedBin, ResolvedBinLevel } from "../../../lib/api-wms-scan";

interface Props {
  level: ResolvedBinLevel;
  onPick: (bin: ResolvedBin) => void;
  onOpenShelf: (address: string) => void;
  onClose: () => void;
}

/** «пусто» / «2 п.» — содержимое ячейки коротко. */
function binContents(bin: ResolvedBin): string {
  return bin.isEmpty ? "пусто" : `${bin.lotsCount} п.`;
}

const TILE =
  "flex min-h-14 flex-col items-start justify-center rounded-xl bg-surface-low px-3 py-2 text-left text-sm transition-colors hover:bg-accent";

export function BinLevelPicker({ level, onPick, onOpenShelf, onClose }: Props) {
  const isRack = level.level === "rack" && (level.shelves?.length ?? 0) > 0;
  const title = isRack
    ? `Стеллаж ${level.address}: ${level.shelves?.length ?? 0} полок — выберите полку`
    : `Полка ${level.address} — выберите ячейку`;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-surface-lowest p-2 ring-ghost">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{title}</span>
        <Button variant="ghost" className="size-12 p-0" onClick={onClose} aria-label="Закрыть выбор">
          <X className="size-4" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
        {isRack
          ? level.shelves?.map((shelf) => (
              <button key={shelf.address} type="button" className={TILE} onClick={() => onOpenShelf(shelf.address)}>
                <span className="flex items-center gap-1 font-mono">
                  {shelf.address}
                  <ChevronRight className="size-3.5" aria-hidden />
                </span>
                <span className="text-xs text-on-surface-variant">
                  {shelf.binsCount} яч. · {shelf.lotsCount} п.
                </span>
              </button>
            ))
          : level.bins.map((bin) => (
              <button key={bin.id} type="button" className={TILE} onClick={() => onPick(bin)}>
                <span className="font-mono">{bin.code}</span>
                <span className="text-xs text-on-surface-variant">{binContents(bin)}</span>
              </button>
            ))}
      </div>
    </div>
  );
}
